import { apiClient } from './apiClient';
import { SIZE_THRESHOLD, presignAndPutFile } from './uploads.service';
import type { ApiResponse, PaginatedResponse } from '@/types/api.types';

/**
 * Legacy QA status codes (see html/lib/patient-details/comm-log-qa.php):
 *   0 = Not in process, 1 = In process, 2 = Unable to resolve, 3 = Resolved.
 * V5 transports these as strings over the wire.
 */
export type QaStatus = 'notInProcess' | 'inProcess' | 'unableToResolve' | 'resolved';

export interface QaItem {
  patientsQaID: number;
  accountID: number;
  patientID: number;
  planID: number | null;
  facilityID: number | null;
  doctorID: number | null;
  qaStatus: QaStatus | null;
  procDate: string | null;
  addDate: string | null;
  qaReason1: number | null;
  patientName: string | null;
  patientEmail: string | null;
  doctorName: string | null;
  planDisplayID: string | null;
  planTypeName: string | null;
  facilityName: string | null;
  reasons: Array<{ qaReasonID: number; title: string }>;
  qaReasonIds?: number[];
}

export interface QaCase extends QaItem {
  qaReason2: number | null;
  qaReason3: number | null;
  qaReason4: number | null;
  qaReason5: number | null;
  notes: string | null;
}

export interface QaFilters {
  patientId?: number;
  qaStatus?: QaStatus | '';
  doctorId?: number;
  doctorIds?: number[];
  facilityId?: number;
  facilityIds?: number[];
  search?: string;
  reasonId?: number;
  reasonIds?: number[];
  /** Assignee token: "user:<userID>" or "group:<roleID>". */
  assignee?: string;
  assignees?: string[];
  /** When true, returns cases assigned to the current user or their groups. */
  myAssigned?: boolean;
  dateFrom?: string;
  dateTo?: string;
  procDateFrom?: string;
  procDateTo?: string;
  page?: number;
  pageSize?: number;
  export?: boolean;
}

export interface CreateQaCaseData {
  patientId: number;
  qaStatus?: QaStatus;
  qaReason1?: number | null;
  qaReasonIds?: number[];
  procDate?: string | null;
  doctorId?: number | null;
  planId?: number | null;
  facilityId?: number | null;
}

export interface UpdateQaCaseData {
  qaStatus?: QaStatus;
  qaReason1?: number | null;
  qaReasonIds?: number[];
  procDate?: string | null;
  doctorID?: number | null;
  doctorId?: number | null;
  planId?: number | null;
  facilityId?: number | null;
}

export function listQaItems(filters: QaFilters = {}): Promise<PaginatedResponse<QaItem>> {
  return apiClient
    .get<PaginatedResponse<QaItem>>('/qa', { params: filters })
    .then((r) => r.data);
}

export function listQaCases(filters: QaFilters = {}): Promise<PaginatedResponse<QaCase>> {
  return apiClient
    .get<PaginatedResponse<QaCase>>('/qa', { params: filters })
    .then((r) => r.data);
}

export function getQaItem(id: number): Promise<QaItem> {
  return apiClient
    .get<ApiResponse<QaItem>>(`/qa/${id}`)
    .then((r) => r.data.data);
}

export function createQaItem(data: {
  patientId: number;
  qaStatus?: QaStatus;
  procDate?: string;
  doctorId?: number;
}): Promise<{ patientsQaId: number }> {
  return apiClient
    .post<ApiResponse<{ patientsQaId: number }>>('/qa', data)
    .then((r) => r.data.data);
}

export function createQaCase(data: CreateQaCaseData): Promise<{ patientsQaId: number }> {
  return apiClient
    .post<ApiResponse<{ patientsQaId: number }>>('/qa', data)
    .then((r) => r.data.data);
}

export function updateQaItem(
  id: number,
  data: Partial<{ qaStatus: QaStatus; procDate: string; doctorID: number }>,
): Promise<{ success: boolean }> {
  return apiClient
    .patch<ApiResponse<{ success: boolean }>>(`/qa/${id}`, data)
    .then((r) => r.data.data);
}

export function updateQaCase(id: number, data: UpdateQaCaseData): Promise<{ success: boolean }> {
  return apiClient
    .patch<ApiResponse<{ success: boolean }>>(`/qa/${id}`, data)
    .then((r) => r.data.data);
}

export function deleteQaCase(id: number): Promise<{ success: boolean }> {
  return apiClient
    .delete<ApiResponse<{ success: boolean }>>(`/qa/${id}`)
    .then((r) => r.data.data);
}

// ─── QA Notes ────────────────────────────────────────────────────────────────

export interface QaNoteAttachment {
  attachmentId: number;
  filename: string;
}

export interface QaNote {
  patientsQaNoteId: number;
  patientsQaId: number;
  patientId: number;
  authorId: number;
  authorName: string;
  note: string;
  dateCreated: string | null;
  attachments: QaNoteAttachment[];
}

export function listQaNotes(caseId: number): Promise<QaNote[]> {
  return apiClient
    .get<ApiResponse<QaNote[]>>(`/qa/${caseId}/notes`)
    .then((r) => r.data.data);
}

export function createQaNote(caseId: number, note: string): Promise<{ patientsQaNoteId: number }> {
  return apiClient
    .post<ApiResponse<{ patientsQaNoteId: number }>>(`/qa/${caseId}/notes`, { note })
    .then((r) => r.data.data);
}

export function deleteQaNote(caseId: number, noteId: number): Promise<void> {
  return apiClient.delete(`/qa/${caseId}/notes/${noteId}`).then(() => undefined);
}

export async function uploadQaNoteAttachment(
  caseId: number,
  noteId: number,
  file: File,
): Promise<{ attachmentId: number; filename: string }> {
  if (file.size > SIZE_THRESHOLD) {
    // Shared helper rather than an inline presign + PUT: the payload arrives
    // wrapped by apiClient's response interceptor, and reading it flat yields an
    // undefined URL that uploads nothing and then posts "undefined" as the key.
    const s3Key = await presignAndPutFile(file);

    const form = new FormData();
    form.append('s3Key', s3Key);
    return apiClient
      .post<ApiResponse<{ attachmentId: number; filename: string }>>(
        `/qa/${caseId}/notes/${noteId}/attachments`,
        form,
      )
      .then((r) => r.data.data);
  }

  const form = new FormData();
  form.append('file', file);
  return apiClient
    .post<ApiResponse<{ attachmentId: number; filename: string }>>(
      `/qa/${caseId}/notes/${noteId}/attachments`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    .then((r) => r.data.data);
}

export async function downloadQaNoteAttachment(
  caseId: number,
  noteId: number,
  attachmentId: number,
  filename: string,
): Promise<void> {
  const response = await apiClient.get(
    `/qa/${caseId}/notes/${noteId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' },
  );
  const blob = response.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function deleteQaNoteAttachment(
  caseId: number,
  noteId: number,
  attachmentId: number,
): Promise<void> {
  return apiClient
    .delete(`/qa/${caseId}/notes/${noteId}/attachments/${attachmentId}`)
    .then(() => undefined);
}

// ─── QA Followers / Assignees ──────────────────────────────────────────────────

export type QaFollowerType = 'user' | 'group';

export interface QaFollower {
  patientsQaFollowerId: number;
  patientsQaId: number;
  followerType: QaFollowerType;
  followerId: number;
  isAssignee: boolean;
  name: string;
}

export function listQaFollowers(caseId: number): Promise<QaFollower[]> {
  return apiClient
    .get<ApiResponse<QaFollower[]>>(`/qa/${caseId}/followers`)
    .then((r) => r.data.data);
}

export function addQaFollower(
  caseId: number,
  data: { followerType: QaFollowerType; followerId: number; isAssignee?: boolean },
): Promise<{ patientsQaFollowerId: number }> {
  return apiClient
    .post<ApiResponse<{ patientsQaFollowerId: number }>>(`/qa/${caseId}/followers`, data)
    .then((r) => r.data.data);
}

export function setQaFollowerAssignee(
  caseId: number,
  followerId: number,
  isAssignee: boolean,
): Promise<void> {
  return apiClient
    .patch(`/qa/${caseId}/followers/${followerId}`, { isAssignee })
    .then(() => undefined);
}

export function removeQaFollower(caseId: number, followerId: number): Promise<void> {
  return apiClient.delete(`/qa/${caseId}/followers/${followerId}`).then(() => undefined);
}
